import { Component, h, Host, Prop } from "@stencil/core";
import { setProps } from "../../utils/utils";

/**
 * @slot - Slot for any content
 * @slot heading - Slot for `stzh-heading` element
 */
@Component({
  tag: "stzh-section",
  styleUrl: "stzh-section.scss",
  shadow: true,
})
export class StzhSection {
  private slotElement: HTMLSlotElement;

  /** Variant */
  @Prop({ reflect: true }) variant: "default" | "highlight" | "contextbox" | "greybox" | "poimax" = "default";

  /** Background color */
  @Prop({ reflect: true }) background: "default" | "secondary" = "default";

  /** Margin top spacing */
  @Prop({ reflect: true }) marginTop: "default" | "none" = "default";

  /** Margin bottom spacing */
  @Prop({ reflect: true }) marginBottom: "default" | "none" = "default";

  /** Padding top spacing */
  @Prop({ reflect: true }) paddingTop: "default" | "none" = "default";

  /** Padding bottom spacing */
  @Prop({ reflect: true }) paddingBottom: "default" | "none" = "default";

  /** Whether to breakout container */
  @Prop({ reflect: true }) breakout: boolean = false;

  /**
   * Searches for any slotted `stzh-cspace` or `stzh-richtext` elements (or their descendants)
   * and sets their properties accordingly.
   */
  private init = () => {
    if (this.variant !== "contextbox" || !this.slotElement) {
      return;
    }

    const assignedElements = this.slotElement.assignedElements({ flatten: true });
    const nestedCspaceOrRichtext: HTMLElement[] = [];

    // Check each assigned element and its descendants for a matching tag name
    assignedElements.forEach((assignedElement: HTMLElement) => {
      if (assignedElement.matches("stzh-cspace, stzh-richtext")) {
        nestedCspaceOrRichtext.push(assignedElement);
      }
      nestedCspaceOrRichtext.push(
        ...Array.from(assignedElement.querySelectorAll<HTMLElement>("stzh-cspace, stzh-richtext")),
      );
    });

    nestedCspaceOrRichtext.forEach(cspaceOrRichtext => {
      setProps(cspaceOrRichtext, {
        removeFirstMargin: true,
        removeLastMargin: true,
      } as HTMLStzhCspaceElement | HTMLStzhRichtextElement);
    });
  };

  private onSlotChange = () => {
    this.init();
  };

  componentDidLoad() {
    this.init();
  }

  render() {
    return (
      <Host>
        <section
          class={{
            "stzh-section": true,
            "stzh-section--is-breakout": this.breakout,
            [`stzh-section--${this.variant}`]: !!this.variant,
            [`stzh-section--margin-bottom-${this.marginBottom}`]: !!this.marginBottom,
          }}
        >
          <div class="stzh-section__outer">
            <div class="stzh-section__heading">
              <slot name="heading"></slot>
            </div>
            <div class="stzh-section__wrapper">
              <slot onSlotchange={this.onSlotChange} ref={el => (this.slotElement = el as HTMLSlotElement)}></slot>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
